import NetxQueryClient, {
	type SuccessResponse,
	type QueryPager,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {getResponseResults} from '@netx/core-next/utils/api';
import {
	getPersonDefaultSort,
	getPersonDefaultFilters,
	type IPerson,
	type IPersonFilter,
	type IPersonSort,
} from '../schema';

/**
 * `getPeople` request params
 * (what is used to send to server)
 */
export type GetPeopleRequestOptions = {
	filters?: Partial<IPersonFilter>;
	sort?: Partial<IPersonSort>;
	startIndex?: number;
	stopIndex?: number;
};

/**
 * `getPeople` request
 * (what gets sent to the server)
 */
export type GetPeopleRequest = [
	// right now we just support the single string filter search on `name`
	IPersonFilter,
	IPersonSort,
	QueryPager,
];

/**
 * `getPeople` response
 * (what comes back from the server)
 */
export type GetPeopleResponse = SuccessResponse<GetPeopleResult>;

/**
 * `getPeople` result
 * (what is utilized from the server response)
 */
export type GetPeopleResult = {
	results: IPerson[];
	size: number;
	startIndex: number;
};

export const getPeopleEndpoint = 'getPeople';

/**
 * Get paged people
 * @param fetchParams Fetch params
 * @param signal Abort signal
 * @returns Promise of paged people
 *
 * @example
 * ```ts
 * const pagedPeople = await getPeople({
 * 	filters: {
 * 		name: 'person name',
 * 		status: 'IDENTIFIED',
 * 	},
 * 	sort: {
 * 		field: 'name',
 * 		order: 'asc',
 * 	},
 * 	startIndex: 0,
 * 	stopIndex: 10,
 * });
 * ```
 */
export const getPeople = async (
	{filters, sort, startIndex = 0, stopIndex = 25}: GetPeopleRequestOptions,
	signal?: AbortSignal,
): Promise<GetPeopleResult> => {
	const client = NetxQueryClient();

	const requestParams: GetPeopleRequest = [
		getPersonDefaultFilters({filters}),
		getPersonDefaultSort(sort),
		{
			startIndex,
			size: stopIndex - startIndex,
		},
	];

	return client
		.post<GetPeopleResponse>(getPeopleEndpoint, requestParams, {
			signal,
		})
		.then(getResponseResults);
};
